import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import { usePasscode } from '../../components/common/PasscodeModal';
import TypeSearch from '../../components/common/TypeSearch';
import { getClientArBalance, computeFifoAllocation } from '../../api/receiptAllocations';
import { loadInvoicesForCommissions, setOffCommissions, reverseSetOff } from '../../api/commissionSetOff';

const fmt = (n) => Number(n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '-';
const PAY_COLOR = { Paid: '#15803d', 'Partially Paid': '#a16207', Unpaid: '#b91c1c', Void: '#6b7280' };

export default function CommissionSetOff() {
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const { prompt: promptPasscode, PasscodeModal } = usePasscode();

  const [tab, setTab] = useState('new');
  const [agents, setAgents] = useState([]);
  const [agent, setAgent] = useState(null);
  const [comms, setComms] = useState([]);
  const [loadingComms, setLoadingComms] = useState(false);
  const [client, setClient] = useState(null);       // { id, label }
  const [suggested, setSuggested] = useState([]);
  const [ar, setAr] = useState(null);
  const [preview, setPreview] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [amountInput, setAmountInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    supabase.from('agents').select('id, tls_code, full_name').eq('is_active', true).order('tls_code').then(({ data }) => setAgents(data || []));
  }, []);

  const pickAgent = useCallback(async (a) => {
    setAgent(a); setClient(null); setAr(null); setSelected(new Set()); setAmountInput(''); setError(''); setSuccess(''); setSuggested([]);
    if (!a) { setComms([]); return; }
    setLoadingComms(true);
    const { data } = await supabase.from('commissions')
      .select('id, reference_id, product_type, commission_amount, settled_amount, commission_status, agent_ref_id, created_at')
      .eq('agent_ref_id', a.id).in('commission_status', ['PENDING', 'APPROVED']).order('created_at', { ascending: true });
    const list = data || [];
    const invMap = await loadInvoicesForCommissions(list);
    setComms(list.map(c => ({ ...c, invoice: invMap[c.reference_id], outstanding: (parseFloat(c.commission_amount) || 0) - (parseFloat(c.settled_amount) || 0) })).filter(c => c.outstanding > 0.005));
    setLoadingComms(false);
    // Suggest client records that look like this agent himself (his personal account).
    const first = (a.full_name || '').split(/\s+/)[0];
    if (first && first.length >= 3) {
      const { data: cs } = await supabase.from('clients').select('id, client_code, full_name').eq('is_active', true)
        .or(`client_code.ilike.%${first}%,full_name.ilike.%${first}%`).limit(8);
      setSuggested((cs || []).map(c => ({ id: c.id, label: `${c.full_name} (${c.client_code})` })));
    }
  }, []);

  const pickClient = useCallback(async (c) => {
    setClient(c); setError(''); setSuccess('');
    if (!c) { setAr(null); return; }
    setAr(await getClientArBalance(c.id));
  }, []);

  const loadHistory = useCallback(async () => {
    const { data } = await supabase.from('commission_setoffs')
      .select('*, agents:agent_ref_id(tls_code, full_name), clients:client_id(full_name, client_code), journal_entries:journal_entry_id(entry_number)')
      .order('created_at', { ascending: false });
    setHistory(data || []);
  }, []);
  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);

  // The user types the amount; it is drawn oldest-first from the ticked commissions, or from all of
  // his unpaid commissions when none are ticked.
  const amount = Math.round((parseFloat(amountInput) || 0) * 100) / 100;
  const drawSet = selected.size ? comms.filter(c => selected.has(c.id)) : comms;
  const available = drawSet.reduce((s, c) => s + c.outstanding, 0);
  const allOutstanding = comms.reduce((s, c) => s + c.outstanding, 0);
  const total = amount;
  const over = ar != null && (amount > ar + 0.01 || amount > available + 0.01);

  // Preview exactly where the selected amount would land (opening balance / which invoices).
  useEffect(() => {
    let live = true;
    if (!client || total <= 0 || over) { setPreview([]); return; }
    computeFifoAllocation(client.id, total).then(p => { if (live) setPreview(p.filter(x => x.target_type !== 'advance')); });
    return () => { live = false; };
  }, [client, total, over]);

  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const useMax = () => { if (ar != null) setAmountInput(String(Math.round(Math.min(available, ar) * 100) / 100)); };

  const handleSetOff = async () => {
    setError(''); setSuccess('');
    try { await promptPasscode(); } catch { return; }
    setBusy(true);
    try {
      const r = await setOffCommissions({ agent, clientId: client.id, amount, commissionIds: selected.size ? [...selected] : undefined, username });
      setSuccess(`Set off LKR ${fmt(r.total)} (${r.entryNumber}) — ${r.commissionsFullySettled} commission(s) fully used, LKR ${fmt(r.commissionLeft)} of his commission still owed to him. ` +
        (r.openingBalanceCleared > 0 ? `LKR ${fmt(r.openingBalanceCleared)} of opening balance cleared. ` : '') +
        (r.invoicesCleared ? `${r.invoicesCleared} invoice(s) paid down.` : ''));
      await pickAgent(agent);
      setClient(client); setAr(await getClientArBalance(client.id));
    } catch (e) { setError(e.message || 'Set-off failed'); }
    setBusy(false);
  };

  const handleReverse = async (so) => {
    const reason = window.prompt('Reason for reversing this set-off?');
    if (!reason?.trim()) return;
    try { await promptPasscode(); } catch { return; }
    setBusy(true); setError('');
    try { await reverseSetOff(so, { reason: reason.trim(), username }); await loadHistory(); }
    catch (e) { setError(e.message || 'Reverse failed'); }
    setBusy(false);
  };

  const card = { background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' };
  const remainingDues = ar != null ? ar - (over ? 0 : total) : null;

  return (
    <div style={{ padding: 24, maxWidth: 1150 }}>
      {PasscodeModal}
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Commission Set-Off</h2>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Settle what an agent owes us (e.g. tickets he bought) against the commissions we owe him</div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        <button className={`btn btn-sm ${tab === 'new' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('new')}>New set-off</button>
        <button className={`btn btn-sm ${tab === 'history' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('history')}>History</button>
      </div>

      {error && <div style={{ color: '#dc2626', marginBottom: 12, fontSize: 13, padding: '8px 12px', background: 'rgba(239,68,68,0.06)', borderRadius: 6 }}>{error}</div>}
      {success && <div style={{ color: '#15803d', marginBottom: 12, fontSize: 13, padding: '8px 12px', background: '#f0fdf4', borderRadius: 6 }}>{success}</div>}

      {tab === 'new' && (
        <>
          <div style={{ ...card, marginBottom: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label className="form-label">1. Agent (owed commission)</label>
              <TypeSearch options={agents.map(a => ({ ...a, label: `${a.tls_code} — ${a.full_name}` }))}
                value={agent ? `${agent.tls_code} — ${agent.full_name}` : ''} displayKey="label" placeholder="TLS code or agent name…"
                onSelect={a => pickAgent(a)} onClear={() => pickAgent(null)} />
              {agent && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>{comms.length} unpaid commission{comms.length === 1 ? '' : 's'} · LKR {fmt(allOutstanding)} owed to him</div>}
            </div>
            <div>
              <label className="form-label">2. Client record that holds his dues</label>
              <TypeSearch
                fetchFn={async (q) => {
                  let qb = supabase.from('clients').select('id, full_name, client_code').eq('is_active', true).limit(30);
                  if (q) qb = qb.or(`full_name.ilike.%${q}%,client_code.ilike.%${q}%`); else qb = qb.order('full_name');
                  const { data } = await qb;
                  return (data || []).map(c => ({ ...c, label: `${c.full_name} (${c.client_code})` }));
                }}
                value={client?.label || ''} displayKey="label" placeholder="Search client…"
                onSelect={c => pickClient({ id: c.id, label: c.label })} onClear={() => pickClient(null)} />
              {agent && !client && suggested.length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Suggested:</span>
                  {suggested.map(s => <button key={s.id} className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => pickClient(s)}>{s.label}</button>)}
                </div>
              )}
              {client && <div style={{ fontSize: 12, marginTop: 6, color: ar > 0 ? '#b91c1c' : 'var(--text-muted)' }}>Currently owes: <strong>LKR {fmt(ar)}</strong></div>}
            </div>
          </div>

          {agent && (
            <div style={{ ...card, padding: 0, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>3. His unpaid commission <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>— tick specific ones to draw from, or leave all unticked to use the oldest first</span></div>
                {selected.size > 0 && <button className="btn btn-secondary btn-sm" onClick={() => setSelected(new Set())}>Clear ticks</button>}
              </div>
              {loadingComms ? <div className="spinner" style={{ margin: 30 }} /> : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px', width: 32 }} />
                    {['Earned', 'Invoice', 'Client', 'Invoice paid?', 'Status', 'Settled'].map(h => <th key={h} style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>{h}</th>)}
                    <th style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>Still owed</th>
                  </tr></thead>
                  <tbody>
                    {comms.map(c => (
                      <tr key={c.id} style={{ borderTop: '1px solid var(--border)', background: selected.has(c.id) ? '#eff6ff' : '' }}>
                        <td style={{ padding: '8px 12px' }}><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} /></td>
                        <td style={{ padding: '8px 12px' }}>{fmtDate(c.created_at)}</td>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{c.invoice?.invoice_number || '—'}</td>
                        <td style={{ padding: '8px 12px' }}>{c.invoice?.clients?.full_name || '—'}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: PAY_COLOR[c.invoice?.payStatus] || 'inherit' }}>{c.invoice?.payStatus || '—'}</td>
                        <td style={{ padding: '8px 12px' }}>{c.commission_status}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>{parseFloat(c.settled_amount) > 0 ? `${fmt(c.settled_amount)} of ${fmt(c.commission_amount)}` : '—'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{fmt(c.outstanding)}</td>
                      </tr>
                    ))}
                    {!comms.length && <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>This agent has no unpaid commissions.</td></tr>}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {agent && client && (
            <div style={{ ...card, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
              <div style={{ fontSize: 13, lineHeight: 1.9 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>4. Amount to set off</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 13 }}>LKR</span>
                  <input className="form-input" type="text" inputMode="decimal" value={amountInput} onChange={e => setAmountInput(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="0.00" style={{ width: 160 }} />
                  <button className="btn btn-secondary btn-sm" onClick={useMax} title="As much as his commission and his dues allow">Max</button>
                </div>
                <div>Client owes now: <strong>LKR {fmt(ar)}</strong> → after: <strong style={{ color: '#b91c1c' }}>LKR {fmt(over ? ar : ar - amount)}</strong></div>
                <div>His commission owed: <strong>LKR {fmt(allOutstanding)}</strong> → after: <strong>LKR {fmt(over ? allOutstanding : allOutstanding - amount)}</strong></div>
                {over && <div style={{ color: '#dc2626' }}>{amount > ar + 0.01 ? 'More than the client owes.' : 'More than the selected commission available.'}</div>}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Posts DR Commission Payable (agent) / CR Accounts Receivable (client) for just this amount. Commissions drawn from are approved first; whatever isn't used stays owed to him.</div>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Clears (oldest first)</div>
                {preview.length ? preview.map((p, i) => <div key={i} style={{ fontSize: 13 }}>{p.label}: <strong>LKR {fmt(p.amount)}</strong></div>)
                  : <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Enter an amount to preview.</div>}
                <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={handleSetOff} disabled={busy || over || total <= 0}>
                  {busy ? 'Posting…' : `Set off LKR ${fmt(total)}`}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'history' && (
        <div style={{ ...card, padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#f8fafc', textAlign: 'left' }}>
              {['Date', 'Agent', 'Client', 'Journal', 'Commissions', 'Amount', ''].map(h => <th key={h} style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)', textAlign: h === 'Amount' ? 'right' : 'left' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id} style={{ borderTop: '1px solid var(--border)', opacity: h.reversed_at ? 0.55 : 1 }}>
                  <td style={{ padding: '9px 12px' }}>{fmtDate(h.created_at)}<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{h.created_by}</div></td>
                  <td style={{ padding: '9px 12px' }}>{h.agents?.tls_code} — {h.agents?.full_name}</td>
                  <td style={{ padding: '9px 12px' }}>{h.clients?.full_name}<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{h.clients?.client_code}</div></td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: 12 }}>{h.journal_entries?.entry_number || '—'}</td>
                  <td style={{ padding: '9px 12px' }}>{h.commission_ids?.length}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600 }}>{fmt(h.amount)}</td>
                  <td style={{ padding: '9px 12px' }}>
                    {h.reversed_at ? <span style={{ color: '#b91c1c', fontSize: 12 }}>Reversed {fmtDate(h.reversed_at)}: {h.reverse_reason}</span>
                      : <button className="btn btn-sm" style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', border: 'none' }} onClick={() => handleReverse(h)} disabled={busy}>Reverse</button>}
                  </td>
                </tr>
              ))}
              {!history.length && <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No set-offs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
